/**
 * Keep the floating voice-agent button clear of a host site's cookie banner.
 *
 * Many sites (incl. FoodFacts) render a cookie-consent banner bottom-right
 * inside an *open shadow DOM*. Because that banner isn't in the light DOM,
 * plain CSS on the host page can't react to it, so the floating "Chat with us"
 * button can end up covering the banner's controls.
 *
 * This watcher finds the banner (even through the shadow root), lifts the
 * button just above it while it's visible, and reverts the instant it's
 * dismissed (so responsive/stylesheet positions are preserved). Only the
 * default floating button is adjusted — "anchored" embeds are left untouched.
 * It's a safe no-op outside the browser and idempotent across re-inits.
 */
export function startCookieClearance() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__ffVoiceCookieClearanceStarted) return;
  window.__ffVoiceCookieClearanceStarted = true;

  let cookieHost = null; // cached open-shadow host of the consent tool
  let lastBottom = null; // last bottom we applied (null = stylesheet default)
  let raf = 0;

  const container = () =>
    document.querySelector('#foodfacts-voice-widget-root .voice-agent-container') ||
    document.querySelector('.voice-agent-container');

  function findCookieHost() {
    if (cookieHost && document.contains(cookieHost) && cookieHost.shadowRoot) return cookieHost;
    cookieHost = null;
    const all = document.querySelectorAll('*');
    for (let i = 0; i < all.length; i++) {
      const sr = all[i].shadowRoot;
      if (!sr) continue;
      try {
        if (/storing of cookies|cookie consent|\bcookies?\b|consent|privacy policy/i.test(sr.textContent || '')) {
          cookieHost = all[i];
          return cookieHost;
        }
      } catch (e) { /* cross-origin or detached — ignore */ }
    }
    return null;
  }

  // Bounding rect of the visible banner inside the shadow root (null if absent).
  function cookieRect() {
    const host = findCookieHost();
    if (!host || !host.shadowRoot) return null;
    const els = host.shadowRoot.querySelectorAll('*');
    const vh = window.innerHeight;
    let best = null;
    for (let j = 0; j < els.length; j++) {
      let cs, r;
      try { cs = getComputedStyle(els[j]); r = els[j].getBoundingClientRect(); } catch (e) { continue; }
      if (r.width > 120 && r.height > 50 && r.height < vh * 0.7 && r.bottom > vh - 380 &&
          cs.visibility !== 'hidden' && cs.display !== 'none' && +cs.opacity !== 0) {
        if (!best || r.height > best.height) best = r; // tallest box = the banner body
      }
    }
    return best;
  }

  function adjust() {
    // Host opt-out (foodfacts.org pins the launcher to the corner itself; lifting it first made it jump = layout shift).
    if (window.FoodFactsVoiceAgent && window.FoodFactsVoiceAgent.cookieClearance === false) return;
    const el = container();
    if (!el) return;
    // Anchored embeds position themselves against a target — don't interfere.
    if (el.classList && el.classList.contains('anchored')) {
      if (lastBottom !== null) { el.style.removeProperty('bottom'); lastBottom = null; }
      return;
    }
    const cr = cookieRect();
    if (cr) {
      const bottom = Math.round(window.innerHeight - cr.top + 16); // sit just above the banner
      if (bottom !== lastBottom) { el.style.setProperty('bottom', bottom + 'px', 'important'); lastBottom = bottom; }
    } else if (lastBottom !== null) {
      el.style.removeProperty('bottom'); // revert to stylesheet (keeps responsive values)
      lastBottom = null;
    }
  }

  const schedule = () => {
    if (raf) return;
    raf = window.requestAnimationFrame(() => { raf = 0; adjust(); });
  };

  if (document.readyState !== 'loading') schedule();
  else document.addEventListener('DOMContentLoaded', schedule);
  try {
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) { /* ignore */ }
  window.addEventListener('resize', schedule);

  // Catch late-loading banners/widgets for the first ~15s.
  let ticks = 0;
  const iv = setInterval(() => { adjust(); if (++ticks > 25) clearInterval(iv); }, 600);
}
